import { useEffect, useState } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { MessageCircle } from "lucide-react";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

/**
 * Lightweight badge in admin header showing today's WhatsApp send count
 * vs the Twilio sandbox 50/day cap. Highlights amber at 80% and red at 100%.
 */
export default function WhatsAppUsageBadge({ getAuthHeaders }) {
  const [usage, setUsage] = useState(null);

  const fetchUsage = async () => {
    try {
      const res = await axios.get(`${API}/admin/whatsapp/usage`, getAuthHeaders());
      setUsage(res.data);
    } catch (_e) { /* silent */ }
  };

  useEffect(() => {
    fetchUsage();
    const id = setInterval(fetchUsage, 60_000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!usage) return null;

  const { today_sent, daily_limit, is_near_limit, is_at_limit } = usage;
  const colorClass = is_at_limit
    ? "bg-red-500/20 text-red-300 border-red-500/30"
    : is_near_limit
      ? "bg-orange-500/20 text-orange-300 border-orange-500/30"
      : "bg-green-500/20 text-green-300 border-green-500/30";

  const tooltip = is_at_limit
    ? "Daily WhatsApp limit reached — outbound messages will fail until tomorrow."
    : is_near_limit
      ? `${daily_limit - today_sent} messages left today.`
      : `${today_sent}/${daily_limit} WhatsApp messages sent today.`;

  return (
    <Badge
      title={tooltip}
      className={`${colorClass} text-[10px] md:text-xs px-1.5 md:px-2.5 cursor-help hidden sm:inline-flex`}
      data-testid="whatsapp-usage-badge"
    >
      <MessageCircle className="w-3 h-3 mr-0.5 md:mr-1" />
      {today_sent}/{daily_limit}
    </Badge>
  );
}
